import { useState, useMemo, useCallback, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useBuilding } from '../Helpers/BuildingContext';
import { useUsersData } from '../Helpers/UsersContext';
import { buildingImagesApi } from '../api/buildingImagesApi';
import { unitsApi } from '../api/unitsApi';
import { PhotoModel } from '../types/protocols';
import { BaseType } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { RELEVANT_BASE_TYPES } from '../Helpers/baseTypeUtils';
import { BaseComponentImpl } from '../types/implementations';
import { getBuildComponentTypeTranslation } from '../Helpers/BuildComponentTypesStore';
import { groupImagesByYear, buildReferencePhotoGroups, componentPlacement, levelName, lockLabel, roomName, unitNameByRoom } from './buildingImageGrouping';
import { MOUNTING_SURFACE_LABELS, MountingSurface, utilityMountingSurface } from '../Helpers/roofPlan';
import type { ReferencePhotoGroup, ComponentPlacement } from './buildingImageGrouping';
import { useFeature } from './useFeatures';

export type { WindowMarker, ReferencePhotoGroup, ComponentPlacement } from './buildingImageGrouping';

// Grouping mode for Fastighetsteknik section
export type UtilityGroupingMode = 'room' | 'category';

/**
 * Base types whose image section is grouped by level/room (or by type, see
 * UtilityGroupingMode) instead of a flat list: Fastighetsteknik and Fast inredning.
 */
export const isRoomGroupedImageSection = (baseType: BaseType | null): boolean =>
    baseType === BaseType.Utility || baseType === BaseType.FixedFurnishing;

// Discriminated union for type-safe image handling
export interface ComponentImage {
    type: 'component';
    photo: PhotoModel;
    componentKey: string;
    baseType: BaseType;
    displayName: string;
    /** Level, room and unit of a room-bound component; absent when it is in no room. */
    placement?: ComponentPlacement;
}

export interface RoomImage {
    type: 'room';
    photo: PhotoModel;
    roomKey: string;
    levelName: string;
    roomNumber: string;
    displayName: string;
}

export type SelectedImage = ComponentImage | RoomImage;

// Year group within a component/room subsection
export interface YearGroup {
    year: string;
    images: SelectedImage[];
}

// Component or room subsection within a base type section
export interface SubSection {
    key: string;
    label: string;
    componentKey: string | null;
    roomKey: string | null;
    /** Level, room and unit of a room-bound component; absent when it is in no room. */
    placement?: ComponentPlacement;
    imagesByYear: YearGroup[];
    totalCount: number;
}

// Room group containing components (for Fastighetsteknik hierarchical view)
export interface RoomGroup {
    roomKey: string;
    roomName: string;
    /** The unit the room belongs to, if any. */
    unitName?: string;
    subSections: SubSection[];
    totalCount: number;
}

// Level group containing rooms (for Fastighetsteknik hierarchical view)
export interface LevelGroup {
    levelKey: string;
    levelName: string;
    rooms: RoomGroup[];
    totalCount: number;
}

// Category group containing components (for Fastighetsteknik category view)
export interface CategoryGroup {
    categoryKey: string;
    categoryName: string;
    subSections: SubSection[];
    totalCount: number;
}

// Top-level section (by base type, or "Rum" for rooms) - without collapse state
export interface ImageSectionData {
    key: string;
    label: string;
    baseType: BaseType | null; // null for room sections
    subSections: SubSection[];
    levelGroups?: LevelGroup[]; // For Fastighetsteknik room/level view
    categoryGroups?: CategoryGroup[]; // For Fastighetsteknik category view
    referencePhotoGroups?: ReferencePhotoGroup[]; // For Fönster photo-first drill-down
    unmarkedSubSections?: SubSection[]; // Windows without any photoLocations pin ("Omarkerade fönster")
    totalCount: number;
}

// Section with collapse state merged in (for rendering)
export interface ImageSection extends ImageSectionData {
    collapsed: boolean;
}

interface UseBuildingImagesProps {
    buildingKey: string;
}

interface UseBuildingImagesReturn {
    // Data
    sections: ImageSection[];
    allImages: SelectedImage[];
    selectedImage: SelectedImage | null;
    selectedIndex: number;
    isAdmin: boolean;

    // Fastighetsteknik grouping mode
    utilityGroupingMode: UtilityGroupingMode;
    setUtilityGroupingMode: (mode: UtilityGroupingMode) => void;

    // Loading and error states
    loading: boolean;
    error: string | null;
    deleting: boolean;

    // Actions
    setSelectedImage: (image: SelectedImage | null) => void;
    toggleSection: (sectionKey: string) => void;
    collapseAll: () => void;
    deleteImage: (image: SelectedImage) => Promise<void>;
    clearError: () => void;
    refetchBuilding: () => Promise<void>;
}

export const useBuildingImages = ({ buildingKey }: UseBuildingImagesProps): UseBuildingImagesReturn => {
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const { users, loadingUsers } = useUsersData();

    // State
    const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
    // Sections start collapsed; the user expands the ones they look at
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [utilityGroupingMode, setUtilityGroupingMode] = useState<UtilityGroupingMode>('room');

    // Check admin status
    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user && users && !loadingUsers) {
                const currentUser = users.find(u => u.key === user.uid);
                setIsAdmin(currentUser?.admin || false);
            } else {
                setIsAdmin(false);
            }
        });

        return () => unsubscribe();
    }, [users, loadingUsers]);

    // Collect and organize images by section (BaseType → Component → Year)
    // Separated from collapse state for better performance
    // Room groups carry no unit names while Lägenheter & lokaler is off
    const unitsEnabled = useFeature('units');
    const sectionData = useMemo((): ImageSectionData[] => {
        if (!currentBuilding?.model?.structure) return [];

        const structure = currentBuilding.model.structure;
        const result: ImageSectionData[] = [];
        const rooms = structure.rooms || {};
        const levels = structure.levels || {};
        const unitNames = unitNameByRoom(unitsEnabled ? unitsApi.parseUnits(currentBuilding.model.units) : {});

        // Process components - group by base type, then by component
        for (const baseType of RELEVANT_BASE_TYPES) {
            const components = structure.components || {};
            const typeComponents = Object.entries(components)
                .filter(([_, component]) => component.type === baseType);

            // Fastighetsteknik and Fast inredning - group by level/room OR category
            if (isRoomGroupedImageSection(baseType)) {
                // Group components by levelKey → roomKey (for room mode)
                const levelMap = new Map<string, Map<string, SubSection[]>>();
                const noRoomComponents: SubSection[] = []; // Components without room assignment
                // Utilities mounted on a roof or facade (parentSurface, no rooms) get their own "Tak"/"Fasad" group
                const mountedComponents: Record<MountingSurface, SubSection[]> = { roof: [], facade: [] };

                // Group components by category (for category mode)
                const categoryMap = new Map<string, SubSection[]>();

                for (const [componentKey, component] of typeComponents) {
                    if (!component.photos || Object.keys(component.photos).length === 0) continue;

                    const componentImpl = new BaseComponentImpl(component);
                    const displayName = componentImpl.displayName(currentBuilding);
                    const placement = componentPlacement(component, structure, unitNames) ?? undefined;

                    const componentImages: SelectedImage[] = Object.values(component.photos).map(photo => ({
                        type: 'component' as const,
                        photo,
                        componentKey,
                        baseType,
                        displayName,
                        placement
                    }));

                    const imagesByYear = groupImagesByYear(componentImages);

                    const subSection: SubSection = {
                        key: `component-${componentKey}`,
                        label: displayName,
                        componentKey,
                        roomKey: null,
                        placement,
                        imagesByYear,
                        totalCount: componentImages.length
                    };

                    // Group by room/level
                    const parentRoomKeys = component.parentRoomKeys?.value;
                    const componentRoomKey = parentRoomKeys && parentRoomKeys.length > 0 ? parentRoomKeys[0] : null;
                    if (componentRoomKey && rooms[componentRoomKey]) {
                        const room = rooms[componentRoomKey];
                        const levelKey = room.levelKey || '__no_level__';

                        if (!levelMap.has(levelKey)) {
                            levelMap.set(levelKey, new Map());
                        }
                        const roomMap = levelMap.get(levelKey)!;

                        if (!roomMap.has(componentRoomKey)) {
                            roomMap.set(componentRoomKey, []);
                        }
                        roomMap.get(componentRoomKey)!.push(subSection);
                    } else {
                        const mounting = utilityMountingSurface(component, components);
                        if (mounting) {
                            mountedComponents[mounting].push(subSection);
                        } else {
                            noRoomComponents.push(subSection);
                        }
                    }

                    // Group by category (specificType)
                    const categoryKey = component.specificType?.value || '__no_category__';
                    if (!categoryMap.has(categoryKey)) {
                        categoryMap.set(categoryKey, []);
                    }
                    categoryMap.get(categoryKey)!.push(subSection);
                }

                // Build levelGroups structure
                const levelGroups: LevelGroup[] = [];

                // Sort levels by level number
                const sortedLevelKeys = Array.from(levelMap.keys()).sort((a, b) => {
                    if (a === '__no_level__') return 1;
                    if (b === '__no_level__') return -1;
                    const levelA = levels[a];
                    const levelB = levels[b];
                    return (levelA?.number || 0) - (levelB?.number || 0);
                });

                for (const levelKey of sortedLevelKeys) {
                    const roomMap = levelMap.get(levelKey)!;
                    const roomGroups: RoomGroup[] = [];
                    for (const [rKey, subSections] of roomMap.entries()) {
                        roomGroups.push({
                            roomKey: rKey,
                            roomName: roomName(rooms[rKey]),
                            unitName: unitNames.get(rKey),
                            subSections,
                            totalCount: subSections.reduce((sum, sub) => sum + sub.totalCount, 0)
                        });
                    }

                    levelGroups.push({
                        levelKey,
                        levelName: levelKey === '__no_level__' ? 'Utan plan' : levelName(levelKey, levels) || 'Utan plan',
                        rooms: roomGroups,
                        totalCount: roomGroups.reduce((sum, rg) => sum + rg.totalCount, 0)
                    });
                }

                // Add "Tak" / "Fasad" groups for mounted utilities
                (['roof', 'facade'] as MountingSurface[]).forEach(mounting => {
                    const subSections = mountedComponents[mounting];
                    if (subSections.length === 0) return;
                    const totalCount = subSections.reduce((sum, sub) => sum + sub.totalCount, 0);
                    levelGroups.push({
                        levelKey: `__mounted_${mounting}__`,
                        levelName: MOUNTING_SURFACE_LABELS[mounting],
                        rooms: [{
                            roomKey: `__mounted_${mounting}__`,
                            roomName: MOUNTING_SURFACE_LABELS[mounting],
                            subSections,
                            totalCount
                        }],
                        totalCount
                    });
                });

                // Add "Utan rum" group if there are components without room assignment
                if (noRoomComponents.length > 0) {
                    levelGroups.push({
                        levelKey: '__no_room__',
                        levelName: 'Utan rum',
                        rooms: [{
                            roomKey: '__unassigned__',
                            roomName: 'Ej tilldelat',
                            subSections: noRoomComponents,
                            totalCount: noRoomComponents.reduce((sum, sub) => sum + sub.totalCount, 0)
                        }],
                        totalCount: noRoomComponents.reduce((sum, sub) => sum + sub.totalCount, 0)
                    });
                }

                // Build categoryGroups structure
                const categoryGroups: CategoryGroup[] = [];

                // Sort categories alphabetically, with "no category" last
                const sortedCategoryKeys = Array.from(categoryMap.keys()).sort((a, b) => {
                    if (a === '__no_category__') return 1;
                    if (b === '__no_category__') return -1;
                    const nameA = getBuildComponentTypeTranslation(a);
                    const nameB = getBuildComponentTypeTranslation(b);
                    return nameA.localeCompare(nameB, 'sv');
                });

                for (const categoryKey of sortedCategoryKeys) {
                    const subSections = categoryMap.get(categoryKey)!;
                    const categoryName = categoryKey === '__no_category__'
                        ? 'Okategoriserat'
                        : getBuildComponentTypeTranslation(categoryKey);

                    categoryGroups.push({
                        categoryKey,
                        categoryName,
                        subSections,
                        totalCount: subSections.reduce((sum, sub) => sum + sub.totalCount, 0)
                    });
                }

                // Only add if there are any components with photos
                const hasContent = levelGroups.length > 0 || categoryGroups.length > 0;
                if (hasContent) {
                    const totalCount = levelGroups.reduce((sum, lg) => sum + lg.totalCount, 0);
                    result.push({
                        key: `basetype-${baseType}`,
                        label: translate(baseType),
                        baseType,
                        subSections: [], // Empty for Utility - using levelGroups/categoryGroups instead
                        levelGroups,
                        categoryGroups,
                        totalCount
                    });
                }
                continue;
            }

            // Special handling for Fönster (Window) - photo-first drill-down.
            // Group windows by the reference photos their photoLocations mark them on,
            // with unmarked windows collected into an "Omarkerade fönster" fallback.
            if (baseType === BaseType.Window) {
                const components = structure.components || {};
                const { groups, unmarked } = buildReferencePhotoGroups(
                    typeComponents,
                    currentBuilding,
                    (component, b) => new BaseComponentImpl(component).displayName(b),
                    // Label a reference photo by the parent component that owns it,
                    // mirroring the PhotoView grouping convention.
                    (parentKey) => {
                        const parentData = parentKey ? components[parentKey] : undefined;
                        return parentData
                            ? new BaseComponentImpl(parentData).displayName(currentBuilding)
                            : 'Foto';
                    }
                );

                const hasContent = groups.length > 0 || unmarked.length > 0;
                if (hasContent) {
                    const groupsTotal = groups.reduce((sum, g) => sum + g.totalCount, 0);
                    const unmarkedTotal = unmarked.reduce((sum, sub) => sum + sub.totalCount, 0);
                    result.push({
                        key: `basetype-${baseType}`,
                        label: translate(baseType),
                        baseType,
                        subSections: [], // Empty - using referencePhotoGroups/unmarkedSubSections instead
                        referencePhotoGroups: groups,
                        unmarkedSubSections: unmarked,
                        totalCount: groupsTotal + unmarkedTotal
                    });
                }
                continue;
            }

            // Standard handling for other base types
            const subSections: SubSection[] = [];

            for (const [componentKey, component] of typeComponents) {
                if (!component.photos || Object.keys(component.photos).length === 0) continue;

                const displayName = baseType === BaseType.LockMechanism
                    ? lockLabel(component, currentBuilding, (c, b) => new BaseComponentImpl(c).displayName(b))
                    : new BaseComponentImpl(component).displayName(currentBuilding);
                const placement = componentPlacement(component, structure, unitNames) ?? undefined;

                const componentImages: SelectedImage[] = Object.values(component.photos).map(photo => ({
                    type: 'component' as const,
                    photo,
                    componentKey,
                    baseType,
                    displayName,
                    placement
                }));

                const imagesByYear = groupImagesByYear(componentImages);

                subSections.push({
                    key: `component-${componentKey}`,
                    label: displayName,
                    componentKey,
                    roomKey: null,
                    placement,
                    imagesByYear,
                    totalCount: componentImages.length
                });
            }

            // Only add base type section if it has components with photos
            if (subSections.length > 0) {
                const totalCount = subSections.reduce((sum, sub) => sum + sub.totalCount, 0);
                result.push({
                    key: `basetype-${baseType}`,
                    label: translate(baseType),
                    baseType,
                    subSections,
                    totalCount
                });
            }
        }

        return result;
    }, [currentBuilding, unitsEnabled]);

    // Merge collapse state with section data (lightweight operation)
    const sections = useMemo((): ImageSection[] => {
        return sectionData.map(section => ({
            ...section,
            collapsed: !expandedSections.has(section.key)
        }));
    }, [sectionData, expandedSections]);

    // Flatten all visible images for keyboard navigation (excludes collapsed sections)
    const allImages = useMemo((): SelectedImage[] => {
        const images: SelectedImage[] = [];
        for (const section of sections) {
            if (section.collapsed) continue;

            // Handle Fastighetsteknik and Fast inredning based on grouping mode
            if (isRoomGroupedImageSection(section.baseType)) {
                if (utilityGroupingMode === 'category' && section.categoryGroups) {
                    // Category mode
                    for (const categoryGroup of section.categoryGroups) {
                        for (const subSection of categoryGroup.subSections) {
                            for (const yearGroup of subSection.imagesByYear) {
                                images.push(...yearGroup.images);
                            }
                        }
                    }
                } else if (section.levelGroups) {
                    // Room/level mode (default)
                    for (const levelGroup of section.levelGroups) {
                        for (const roomGroup of levelGroup.rooms) {
                            for (const subSection of roomGroup.subSections) {
                                for (const yearGroup of subSection.imagesByYear) {
                                    images.push(...yearGroup.images);
                                }
                            }
                        }
                    }
                }
                continue;
            }

            // Fönster: only the unmarked ("Omarkerade") windows render as flat thumbnails
            // here. Marked windows live behind the drill-down, so the page scopes
            // arrow-key navigation to the active window rather than this flat list.
            if (section.baseType === BaseType.Window) {
                for (const subSection of section.unmarkedSubSections || []) {
                    for (const yearGroup of subSection.imagesByYear) {
                        images.push(...yearGroup.images);
                    }
                }
                continue;
            }

            // Handle flat subSections (for other base types)
            for (const subSection of section.subSections) {
                for (const yearGroup of subSection.imagesByYear) {
                    images.push(...yearGroup.images);
                }
            }
        }
        return images;
    }, [sections, utilityGroupingMode]);

    // Cache selected index for O(1) keyboard navigation
    const selectedIndex = useMemo((): number => {
        if (!selectedImage) return -1;
        return allImages.findIndex(img => img.photo.key === selectedImage.photo.key);
    }, [allImages, selectedImage]);

    // Collapse all sections
    const collapseAll = useCallback(() => {
        setExpandedSections(new Set());
    }, []);

    // Toggle section collapse
    const toggleSection = useCallback((sectionKey: string) => {
        setExpandedSections(prev => {
            const next = new Set(prev);
            if (next.has(sectionKey)) {
                next.delete(sectionKey);
            } else {
                next.add(sectionKey);
            }
            return next;
        });
    }, []);

    // Delete image
    const deleteImage = useCallback(async (image: SelectedImage) => {
        if (!buildingKey) {
            setError('Byggnadsnyckel saknas');
            return;
        }

        setDeleting(true);
        setError(null);

        try {
            if (image.type === 'component') {
                await buildingImagesApi.deleteComponentPhoto(
                    buildingKey,
                    image.componentKey,
                    image.photo.key
                );
            } else {
                await buildingImagesApi.deleteRoomPhoto(
                    buildingKey,
                    image.roomKey,
                    image.photo.key
                );
            }

            // Clear selection if the deleted image was selected
            if (selectedImage?.photo.key === image.photo.key) {
                setSelectedImage(null);
            }

            // Refetch building to update the images
            await fetchBuilding(buildingKey);
        } catch (err: unknown) {
            console.error('Error deleting image:', err);
            const message = err instanceof Error ? err.message : 'Kunde inte ta bort bilden';
            setError(message);
        } finally {
            setDeleting(false);
        }
    }, [buildingKey, selectedImage, fetchBuilding]);

    // Refetch building data to update images
    const refetchBuilding = useCallback(async () => {
        if (buildingKey) {
            await fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        sections,
        allImages,
        selectedImage,
        selectedIndex,
        isAdmin,
        utilityGroupingMode,
        setUtilityGroupingMode,
        loading: loadingBuilding || loadingUsers,
        error: error || buildingError || null,
        deleting,
        setSelectedImage,
        toggleSection,
        collapseAll,
        deleteImage,
        clearError,
        refetchBuilding
    };
};
